import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { useAppDispatch } from 'src/app/hooks';
import { appApiSlice } from 'src/api/app-ApiSlice';
import { TAG_FLOOR_ROOMS_AND_RELATED, TAG_GET_DEVICES, TAG_GET_PRODUCTS_OF_ROOMS_BY_FLOORS } from 'src/api/api-tags';
import { useGetAutoplanTutorialStatusQuery, useSetAutoplanTutorialCompleteMutation } from 'src/api/v2/user-v2-ApiSlice';
import { showSnackBar } from 'src/api/ui-slice';
import { startAutoPlanWizard } from 'src/components/Planner/planner/wizard';
import { AutoPlanBundle, StartAutoPlanModal } from 'src/pages/PlannerPage/Walkthrough/StartAutoPlanModal';
import { FirstOfferReadyModal } from 'src/pages/PlannerPage/Walkthrough/FirstOfferReadyModal';
import { ts } from 'src/utils/translate';

enum WalkthroughStep {
    CHOOSING = 'CHOOSING',
    RUNNING = 'RUNNING',
    AWAITING_OFFER = 'AWAITING_OFFER',
}

interface FirstWalkthroughProps {
    offerButtonEl: HTMLElement | null;
    onFinished: () => void;
}

export function FirstWalkthrough({ offerButtonEl, onFinished }: FirstWalkthroughProps) {
    const dispatch = useAppDispatch();
    const { data: status, isLoading, isError } = useGetAutoplanTutorialStatusQuery();
    const [setComplete] = useSetAutoplanTutorialCompleteMutation();
    const [step, setStep] = useState<WalkthroughStep>(WalkthroughStep.CHOOSING);

    const alreadyCompleted = status?.completed === true;

    useEffect(() => {
        if (alreadyCompleted || isError) onFinished();
    }, [alreadyCompleted, isError, onFinished]);

    const handleAutoPlanPlaced = () => {
        dispatch(appApiSlice.util.invalidateTags([TAG_GET_DEVICES, TAG_FLOOR_ROOMS_AND_RELATED, TAG_GET_PRODUCTS_OF_ROOMS_BY_FLOORS]));
        setStep(WalkthroughStep.AWAITING_OFFER);
    };

    const handleAutoPlanFailed = () => {
        dispatch(showSnackBar({
            text: ts('start_auto_plan_failed', 'Auto-plan could not be completed. You can place devices manually.'),
            severity: 'error',
        }));
        onFinished();
    };

    const handleSelect = (bundle: AutoPlanBundle) => {
        setStep(WalkthroughStep.RUNNING);
        startAutoPlanWizard(bundle, handleAutoPlanPlaced, handleAutoPlanFailed);
    };

    useEffect(() => {
        if (step !== WalkthroughStep.AWAITING_OFFER || offerButtonEl == null) return;
        const prevZIndex = offerButtonEl.style.zIndex;
        offerButtonEl.style.zIndex = '10000';
        const markComplete = () => {
            setComplete().unwrap().catch((error) => console.error('Failed to mark autoplan tutorial complete', error));
        };
        offerButtonEl.addEventListener('click', markComplete);
        return () => {
            offerButtonEl.style.zIndex = prevZIndex;
            offerButtonEl.removeEventListener('click', markComplete);
        };
    }, [step, offerButtonEl, setComplete]);

    if (isLoading || alreadyCompleted || isError) return null;

    switch (step) {
        case WalkthroughStep.CHOOSING:
            return <StartAutoPlanModal onSelect={handleSelect} onClose={onFinished} />;
        case WalkthroughStep.RUNNING:
            return <Box sx={{ position: 'fixed', inset: 0, zIndex: 10001 }} />;
        case WalkthroughStep.AWAITING_OFFER:
            return (
                <>
                    <Box sx={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.3)', zIndex: 9998 }} />
                    <FirstOfferReadyModal anchorEl={offerButtonEl} />
                </>
            );
    }
}
